// Trigonometry in 2D and 3D Problems Knowledge Graph Samples
// Organized by difficulty levels with systematic naming

export const trigonometry2D3DSamples = {
  fundamental: [
    {
      id: 'trig2d3d_fund_01',
      problem: "Find the height of a tree if the angle of elevation from a point 20 meters away is 30°",
      problemType: "trigonometry_2d_3d",
      difficulty: "fundamental",
      description: "Basic angle of elevation problem",
      graphData: {
        nodes: [
          {
            id: "distance",
            name: "Distance",
            type: "concepts",
            description: "Distance from observer to tree",
            value: "20 meters",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "angle",
            name: "Angle of Elevation",
            type: "concepts",
            description: "Angle from horizontal to tree top",
            value: "30°",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "tangent_formula",
            name: "Tangent Formula",
            type: "formulas",
            description: "tan(θ) = opposite/adjacent",
            value: "tan(θ) = opposite/adjacent",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step1_identify_values",
            name: "Step 1: Identify Values",
            type: "steps",
            description: "Identify distance and angle",
            value: "Step 1: Distance = 20m, angle = 30°",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Apply tangent formula",
            value: "Step 2: tan(30°) = height/20",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_calculate_height",
            name: "Step 3: Calculate Height",
            type: "steps",
            description: "Calculate the height",
            value: "Step 3: height = 20 × tan(30°) = 11.54m",
            importance: 10,
            x: 100, y: 50, z: 25
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The height of the tree",
            value: "11.54 meters",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "distance", to: "tangent_formula", type: "used in", strength: 9 },
          { from: "angle", to: "tangent_formula", type: "used in", strength: 9 },
          { from: "distance", to: "step1_identify_values", type: "used in", strength: 9 },
          { from: "angle", to: "step1_identify_values", type: "used in", strength: 9 },
          { from: "tangent_formula", to: "step2_apply_formula", type: "used in", strength: 10 },
          { from: "step1_identify_values", to: "step2_apply_formula", type: "leads to", strength: 10 },
          { from: "step2_apply_formula", to: "step3_calculate_height", type: "leads to", strength: 10 },
          { from: "step3_calculate_height", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'trig2d3d_fund_02',
      problem: "Find the length of a ladder leaning against a wall if it makes an angle of 60° with the ground and reaches 8 meters up the wall",
      problemType: "trigonometry_2d_3d",
      difficulty: "fundamental",
      description: "Basic ladder problem using sine",
      graphData: {
        nodes: [
          {
            id: "height",
            name: "Height",
            type: "concepts",
            description: "Height reached on wall",
            value: "8 meters",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "angle",
            name: "Angle",
            type: "concepts",
            description: "Angle with ground",
            value: "60°",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "sine_formula",
            name: "Sine Formula",
            type: "formulas",
            description: "sin(θ) = opposite/hypotenuse",
            value: "sin(60°) = 8/ladder_length",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate ladder length",
            value: "ladder_length = 8/sin(60°) = 8/0.866 = 9.24",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The length of the ladder",
            value: "9.24 meters",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "height", to: "sine_formula", type: "used in", strength: 9 },
          { from: "angle", to: "sine_formula", type: "used in", strength: 9 },
          { from: "sine_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'trig2d3d_fund_03',
      problem: "Find the distance across a river if a surveyor measures an angle of 45° from one bank to a point on the opposite bank, and the distance along the first bank is 50 meters",
      problemType: "trigonometry_2d_3d",
      difficulty: "fundamental",
      description: "Basic river crossing problem",
      graphData: {
        nodes: [
          {
            id: "distance_along_bank",
            name: "Distance Along Bank",
            type: "concepts",
            description: "Distance measured along first bank",
            value: "50 meters",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "angle",
            name: "Angle",
            type: "concepts",
            description: "Angle to opposite bank",
            value: "45°",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "tangent_formula",
            name: "Tangent Formula",
            type: "formulas",
            description: "tan(θ) = opposite/adjacent",
            value: "tan(45°) = river_width/50",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate river width",
            value: "river_width = 50 × tan(45°) = 50 × 1 = 50",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The width of the river",
            value: "50 meters",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "distance_along_bank", to: "tangent_formula", type: "used in", strength: 9 },
          { from: "angle", to: "tangent_formula", type: "used in", strength: 9 },
          { from: "tangent_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'trig2d3d_fund_04',
      problem: "Find the angle of depression from the top of a 30-meter building to a car 20 meters away",
      problemType: "trigonometry_2d_3d",
      difficulty: "fundamental",
      description: "Basic angle of depression problem",
      graphData: {
        nodes: [
          {
            id: "height",
            name: "Building Height",
            type: "concepts",
            description: "Height of the building",
            value: "30 meters",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "distance",
            name: "Horizontal Distance",
            type: "concepts",
            description: "Distance to the car",
            value: "20 meters",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "tangent_formula",
            name: "Tangent Formula",
            type: "formulas",
            description: "tan(θ) = opposite/adjacent",
            value: "tan(θ) = 30/20 = 1.5",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate angle",
            value: "θ = tan⁻¹(1.5) = 56.31°",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The angle of depression",
            value: "56.31°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "height", to: "tangent_formula", type: "used in", strength: 9 },
          { from: "distance", to: "tangent_formula", type: "used in", strength: 9 },
          { from: "tangent_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'trig2d3d_fund_05',
      problem: "Find the length of a shadow cast by a 5-meter pole when the sun is at an angle of elevation of 30°",
      problemType: "trigonometry_2d_3d",
      difficulty: "fundamental",
      description: "Basic shadow length problem",
      graphData: {
        nodes: [
          {
            id: "pole_height",
            name: "Pole Height",
            type: "concepts",
            description: "Height of the pole",
            value: "5 meters",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "sun_angle",
            name: "Sun Angle",
            type: "concepts",
            description: "Angle of elevation of sun",
            value: "30°",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "tangent_formula",
            name: "Tangent Formula",
            type: "formulas",
            description: "tan(θ) = opposite/adjacent",
            value: "tan(30°) = 5/shadow_length",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate shadow length",
            value: "shadow_length = 5/tan(30°) = 5/0.577 = 8.66",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The length of the shadow",
            value: "8.66 meters",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "pole_height", to: "tangent_formula", type: "used in", strength: 9 },
          { from: "sun_angle", to: "tangent_formula", type: "used in", strength: 9 },
          { from: "tangent_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  intermediate: [
    {
      id: 'trig2d3d_inter_01',
      problem: "A plane is flying at an altitude of 3000 meters. The pilot sees a mountain peak at an angle of elevation of 15°. If the plane is 10 km from the mountain, find the height of the mountain",
      problemType: "trigonometry_2d_3d",
      difficulty: "intermediate",
      description: "Mountain height from plane",
      graphData: {
        nodes: [
          {
            id: "plane_altitude",
            name: "Plane Altitude",
            type: "concepts",
            description: "Altitude of the plane",
            value: "3000 meters",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "distance",
            name: "Distance to Mountain",
            type: "concepts",
            description: "Horizontal distance to mountain",
            value: "10 km = 10000 meters",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "angle",
            name: "Angle of Elevation",
            type: "concepts",
            description: "Angle to mountain peak",
            value: "15°",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "tangent_formula",
            name: "Tangent Formula",
            type: "formulas",
            description: "tan(θ) = opposite/adjacent",
            value: "tan(15°) = (mountain_height - 3000)/10000",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate mountain height",
            value: "mountain_height = 3000 + 10000 × tan(15°) = 3000 + 2679 = 5679",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The height of the mountain",
            value: "5679 meters",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "plane_altitude", to: "tangent_formula", type: "used in", strength: 9 },
          { from: "distance", to: "tangent_formula", type: "used in", strength: 9 },
          { from: "angle", to: "tangent_formula", type: "used in", strength: 9 },
          { from: "tangent_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'trig2d3d_inter_02',
      problem: "Find the angle between two forces of 8 N and 6 N acting at right angles to each other, if their resultant is 10 N",
      problemType: "trigonometry_2d_3d",
      difficulty: "intermediate",
      description: "Vector forces problem",
      graphData: {
        nodes: [
          {
            id: "force1",
            name: "First Force",
            type: "concepts",
            description: "First force magnitude",
            value: "8 N",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "force2",
            name: "Second Force",
            type: "concepts",
            description: "Second force magnitude",
            value: "6 N",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "resultant",
            name: "Resultant Force",
            type: "concepts",
            description: "Resultant force magnitude",
            value: "10 N",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "cosine_formula",
            name: "Cosine Formula",
            type: "formulas",
            description: "R² = F₁² + F₂² + 2F₁F₂cos(θ)",
            value: "10² = 8² + 6² + 2×8×6×cos(θ)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate angle",
            value: "100 = 64 + 36 + 96cos(θ), cos(θ) = 0, θ = 90°",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The angle between forces",
            value: "90°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "force1", to: "cosine_formula", type: "used in", strength: 9 },
          { from: "force2", to: "cosine_formula", type: "used in", strength: 9 },
          { from: "resultant", to: "cosine_formula", type: "used in", strength: 9 },
          { from: "cosine_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'trig2d3d_inter_03',
      problem: "A ship is sailing at 20 km/h in a direction 30° north of east. Find its north and east components",
      problemType: "trigonometry_2d_3d",
      difficulty: "intermediate",
      description: "Vector component resolution",
      graphData: {
        nodes: [
          {
            id: "velocity",
            name: "Velocity",
            type: "concepts",
            description: "Ship's velocity",
            value: "20 km/h",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "angle",
            name: "Direction Angle",
            type: "concepts",
            description: "Angle north of east",
            value: "30°",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "east_component",
            name: "East Component",
            type: "formulas",
            description: "V_east = V × cos(θ)",
            value: "V_east = 20 × cos(30°)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "north_component",
            name: "North Component",
            type: "formulas",
            description: "V_north = V × sin(θ)",
            value: "V_north = 20 × sin(30°)",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "east_calculation",
            name: "East Calculation",
            type: "values",
            description: "Calculate east component",
            value: "V_east = 20 × 0.866 = 17.32 km/h",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "north_calculation",
            name: "North Calculation",
            type: "values",
            description: "Calculate north component",
            value: "V_north = 20 × 0.5 = 10 km/h",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "velocity", to: "east_component", type: "used in", strength: 9 },
          { from: "angle", to: "east_component", type: "used in", strength: 9 },
          { from: "velocity", to: "north_component", type: "used in", strength: 9 },
          { from: "angle", to: "north_component", type: "used in", strength: 9 },
          { from: "east_component", to: "east_calculation", type: "gives", strength: 10 },
          { from: "north_component", to: "north_calculation", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'trig2d3d_inter_04',
      problem: "Find the area of a triangle with sides 5 cm, 7 cm, and the included angle of 60°",
      problemType: "trigonometry_2d_3d",
      difficulty: "intermediate",
      description: "Triangle area using trigonometry",
      graphData: {
        nodes: [
          {
            id: "side1",
            name: "First Side",
            type: "concepts",
            description: "First side length",
            value: "5 cm",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "side2",
            name: "Second Side",
            type: "concepts",
            description: "Second side length",
            value: "7 cm",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "included_angle",
            name: "Included Angle",
            type: "concepts",
            description: "Angle between sides",
            value: "60°",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "area_formula",
            name: "Area Formula",
            type: "formulas",
            description: "Area = ½ × a × b × sin(C)",
            value: "Area = ½ × 5 × 7 × sin(60°)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate area",
            value: "Area = ½ × 35 × 0.866 = 15.16",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The area of the triangle",
            value: "15.16 cm²",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "side1", to: "area_formula", type: "used in", strength: 9 },
          { from: "side2", to: "area_formula", type: "used in", strength: 9 },
          { from: "included_angle", to: "area_formula", type: "used in", strength: 9 },
          { from: "area_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'trig2d3d_inter_05',
      problem: "A ladder 10 meters long leans against a wall. If the foot of the ladder is 6 meters from the wall, find the angle the ladder makes with the ground",
      problemType: "trigonometry_2d_3d",
      difficulty: "intermediate",
      description: "Ladder angle problem",
      graphData: {
        nodes: [
          {
            id: "ladder_length",
            name: "Ladder Length",
            type: "concepts",
            description: "Length of the ladder",
            value: "10 meters",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "distance_from_wall",
            name: "Distance from Wall",
            type: "concepts",
            description: "Distance of ladder foot from wall",
            value: "6 meters",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "cosine_formula",
            name: "Cosine Formula",
            type: "formulas",
            description: "cos(θ) = adjacent/hypotenuse",
            value: "cos(θ) = 6/10 = 0.6",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate angle",
            value: "θ = cos⁻¹(0.6) = 53.13°",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The angle with the ground",
            value: "53.13°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "ladder_length", to: "cosine_formula", type: "used in", strength: 9 },
          { from: "distance_from_wall", to: "cosine_formula", type: "used in", strength: 9 },
          { from: "cosine_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  harder: [
    {
      id: 'trig2d3d_hard_01',
      problem: "Find the height of a building if from two points A and B, 50 meters apart, the angles of elevation are 30° and 45° respectively",
      problemType: "trigonometry_2d_3d",
      difficulty: "harder",
      description: "Building height from two observation points",
      graphData: {
        nodes: [
          {
            id: "distance_ab",
            name: "Distance AB",
            type: "concepts",
            description: "Distance between observation points",
            value: "50 meters",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "angle_a",
            name: "Angle at A",
            type: "concepts",
            description: "Angle of elevation from point A",
            value: "30°",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "angle_b",
            name: "Angle at B",
            type: "concepts",
            description: "Angle of elevation from point B",
            value: "45°",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "equation1",
            name: "Equation 1",
            type: "formulas",
            description: "tan(30°) = h/x",
            value: "0.577 = h/x",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "equation2",
            name: "Equation 2",
            type: "formulas",
            description: "tan(45°) = h/(x+50)",
            value: "1 = h/(x+50)",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "Solve simultaneous equations",
            value: "h = 68.3 meters",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "angle_a", to: "equation1", type: "gives", strength: 9 },
          { from: "angle_b", to: "equation2", type: "gives", strength: 9 },
          { from: "distance_ab", to: "equation2", type: "used in", strength: 9 },
          { from: "equation1", to: "solution", type: "used in", strength: 9 },
          { from: "equation2", to: "solution", type: "used in", strength: 9 }
        ]
      }
    },
    {
      id: 'trig2d3d_hard_02',
      problem: "A plane is flying at 500 km/h in a direction 60° north of east. A wind is blowing at 100 km/h from the west. Find the plane's actual velocity",
      problemType: "trigonometry_2d_3d",
      difficulty: "harder",
      description: "Vector addition with wind",
      graphData: {
        nodes: [
          {
            id: "plane_velocity",
            name: "Plane Velocity",
            type: "concepts",
            description: "Plane's velocity relative to air",
            value: "500 km/h at 60°",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "wind_velocity",
            name: "Wind Velocity",
            type: "concepts",
            description: "Wind velocity from west",
            value: "100 km/h west",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "plane_components",
            name: "Plane Components",
            type: "values",
            description: "Resolve plane velocity",
            value: "V_east = 500cos(60°) = 250, V_north = 500sin(60°) = 433",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "wind_components",
            name: "Wind Components",
            type: "values",
            description: "Wind components",
            value: "V_east = -100, V_north = 0",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "resultant_components",
            name: "Resultant Components",
            type: "values",
            description: "Add components",
            value: "V_east = 150, V_north = 433",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "final_velocity",
            name: "Final Velocity",
            type: "values",
            description: "Calculate magnitude and direction",
            value: "V = √(150² + 433²) = 458 km/h at 71°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "plane_velocity", to: "plane_components", type: "resolved to", strength: 9 },
          { from: "wind_velocity", to: "wind_components", type: "resolved to", strength: 9 },
          { from: "plane_components", to: "resultant_components", type: "added to", strength: 9 },
          { from: "wind_components", to: "resultant_components", type: "added to", strength: 9 },
          { from: "resultant_components", to: "final_velocity", type: "gives", strength: 10 }
        ]
      }
    },
    {
      id: 'trig2d3d_hard_03',
      problem: "Find the volume of a cone with radius 4 cm and height 6 cm using integration",
      problemType: "trigonometry_2d_3d",
      difficulty: "harder",
      description: "Cone volume using calculus",
      graphData: {
        nodes: [
          {
            id: "radius",
            name: "Radius",
            type: "concepts",
            description: "Base radius of cone",
            value: "4 cm",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "height",
            name: "Height",
            type: "concepts",
            description: "Height of cone",
            value: "6 cm",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "radius_function",
            name: "Radius Function",
            type: "formulas",
            description: "r(h) = 4(1 - h/6)",
            value: "r = 4(1 - h/6)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "volume_integral",
            name: "Volume Integral",
            type: "formulas",
            description: "V = π∫₀⁶ r² dh",
            value: "V = π∫₀⁶ [4(1-h/6)]² dh",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Evaluate integral",
            value: "V = π∫₀⁶ 16(1-h/6)² dh = 32π",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The volume of the cone",
            value: "100.53 cm³",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "radius", to: "radius_function", type: "used in", strength: 9 },
          { from: "height", to: "radius_function", type: "used in", strength: 9 },
          { from: "radius_function", to: "volume_integral", type: "used in", strength: 9 },
          { from: "volume_integral", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'trig2d3d_hard_04',
      problem: "Find the angle between the diagonal of a cube and one of its edges",
      problemType: "trigonometry_2d_3d",
      difficulty: "harder",
      description: "3D geometry with trigonometry",
      graphData: {
        nodes: [
          {
            id: "cube_edge",
            name: "Cube Edge",
            type: "concepts",
            description: "Length of cube edge",
            value: "a units",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "face_diagonal",
            name: "Face Diagonal",
            type: "values",
            description: "Diagonal of cube face",
            value: "d_face = a√2",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "space_diagonal",
            name: "Space Diagonal",
            type: "values",
            description: "Diagonal through cube",
            value: "d_space = a√3",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "cosine_formula",
            name: "Cosine Formula",
            type: "formulas",
            description: "cos(θ) = adjacent/hypotenuse",
            value: "cos(θ) = a/(a√3)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate angle",
            value: "cos(θ) = 1/√3, θ = cos⁻¹(1/√3) = 54.74°",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The angle between diagonal and edge",
            value: "54.74°",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "cube_edge", to: "face_diagonal", type: "used to calculate", strength: 9 },
          { from: "cube_edge", to: "space_diagonal", type: "used to calculate", strength: 9 },
          { from: "cube_edge", to: "cosine_formula", type: "used in", strength: 9 },
          { from: "space_diagonal", to: "cosine_formula", type: "used in", strength: 9 },
          { from: "cosine_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'trig2d3d_hard_05',
      problem: "Find the shortest distance from a point (3, 4, 5) to the plane 2x + 3y + 6z = 12",
      problemType: "trigonometry_2d_3d",
      difficulty: "harder",
      description: "Point to plane distance in 3D",
      graphData: {
        nodes: [
          {
            id: "point",
            name: "Point",
            type: "concepts",
            description: "Given point in 3D",
            value: "(3, 4, 5)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "plane_equation",
            name: "Plane Equation",
            type: "concepts",
            description: "Equation of the plane",
            value: "2x + 3y + 6z = 12",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "normal_vector",
            name: "Normal Vector",
            type: "values",
            description: "Normal vector of plane",
            value: "n = (2, 3, 6)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "distance_formula",
            name: "Distance Formula",
            type: "formulas",
            description: "d = |ax₀ + by₀ + cz₀ - d|/√(a² + b² + c²)",
            value: "d = |2(3) + 3(4) + 6(5) - 12|/√(4 + 9 + 36)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate distance",
            value: "d = |6 + 12 + 30 - 12|/√49 = 36/7 = 5.14",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The shortest distance",
            value: "5.14 units",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "point", to: "distance_formula", type: "used in", strength: 9 },
          { from: "plane_equation", to: "normal_vector", type: "gives", strength: 9 },
          { from: "normal_vector", to: "distance_formula", type: "used in", strength: 9 },
          { from: "distance_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 }
        ]
      }
    }
  ]
}; 